import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefCallback } from "react";
import { Badge } from "./ui";
import { FramePreview } from "./FramePreview";
import { OverlaySettings } from "./OverlaySettings";

export interface ViewTransform { scale: number; x: number; y: number }

/**
 * One eye, with its overlays.
 *
 * The overlay canvas sits inside the same transformed wrapper as the video and
 * is sized in *source pixels* — so landmark coordinates from the analysis, which
 * are in the original recording's pixels, are drawn with no coordinate mapping
 * at all and stay correct under zoom, pan and any letterboxing.
 */
export function VideoPanel({
  label,
  url,
  colour,
  register,
  onLoadedMetadata,
  transform,
  onTransform,
  frameInfo,
  badges,
  muted = true,
  sourceSize,
  drawOverlay,
  layers,
  onProbe,
  footer,
  framePreview,
}: {
  label: string;
  url: string | null;
  colour: string;
  register: RefCallback<HTMLVideoElement>;
  onLoadedMetadata: () => void;
  transform: ViewTransform;
  onTransform: (t: ViewTransform) => void;
  frameInfo?: { index: number; deltaMs: number | null } | null;
  badges?: ReactNode;
  muted?: boolean;
  sourceSize?: [number, number] | null;
  drawOverlay?: (ctx: CanvasRenderingContext2D, width: number, height: number, sourcePixelsPerCssPixel: number) => void;
  /** Extra elements drawn inside the picture box, under the overlay canvas. */
  layers?: ReactNode;
  onProbe?: (nx: number, ny: number) => void;
  footer?: ReactNode;
  framePreview?: {url: string; nearby: string[]; onReady: (url: string | null) => void};
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dragRef = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);
  const [box, setBox] = useState<{ w: number; h: number } | null>(null);
  const [previewReady, setPreviewReady] = useState<string | null>(null);
  const [mediaReady, setMediaReady] = useState<string | null>(null);
  const frameVisible = !!url && (framePreview ? previewReady === framePreview.url : mediaReady === url);
  const attachVideo = useCallback((element: HTMLVideoElement | null) => {
    videoRef.current = element;
    register(element);
  }, [register]);
  const updateMediaReady = () => {
    const video = videoRef.current;
    setMediaReady(video && video.readyState >= 2 && !video.seeking && !video.error ? url : null);
  };

  /* The picture box, measured rather than negotiated.
   *
   * CSS cannot express "fit this aspect ratio inside this box" in a way that
   * also gives the overlays something to attach to: `max-width/max-height` on
   * the media leaves the wrapper a different size than the picture, and
   * `aspect-ratio` is ignored once both width and height are set. Measuring the
   * container and computing the contained rect is unambiguous, and it is what
   * makes `inset: 0` on the canvas mean exactly the picture. */
  useEffect(() => {
    const host = hostRef.current;
    if (!host || !sourceSize) {
      setBox(null);
      return;
    }
    const aspect = sourceSize[0] / sourceSize[1];
    const compute = () => {
      const cw = host.clientWidth;
      const ch = host.clientHeight;
      if (cw <= 0 || ch <= 0) return;
      // Round one side and derive the other, so the box keeps the source
      // aspect exactly rather than drifting by a pixel in each direction.
      let w: number;
      let h: number;
      if (cw / aspect <= ch) {
        w = Math.round(cw);
        h = Math.round(w / aspect);
      } else {
        h = Math.round(ch);
        w = Math.round(h * aspect);
      }
      setBox(previous => previous?.w === w && previous?.h === h ? previous : { w: Math.max(1, w), h: Math.max(1, h) });
    };
    const observer = new ResizeObserver(compute);
    observer.observe(host);
    compute();
    return () => observer.disconnect();
  }, [sourceSize?.[0], sourceSize?.[1]]);

  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey && !e.shiftKey) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left - rect.width / 2;
      const cy = e.clientY - rect.top - rect.height / 2;
      const factor = Math.exp(-e.deltaY * 0.0022);
      const scale = Math.min(12, Math.max(1, transform.scale * factor));
      const k = scale / transform.scale;
      onTransform({
        scale,
        x: scale === 1 ? 0 : cx - (cx - transform.x) * k,
        y: scale === 1 ? 0 : cy - (cy - transform.y) * k,
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [transform, onTransform]);

  /* Redraw the overlay whenever the caller's draw function changes. */
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !sourceSize) return;
    const [w, h] = sourceSize;
    const scale = Math.min(1, ((box?.w ?? w) * Math.min(2, devicePixelRatio)) / w);
    const cw = Math.round(w * scale), ch = Math.round(h * scale);
    if (canvas.width !== cw || canvas.height !== ch) { canvas.width = cw; canvas.height = ch; }
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.scale(scale, scale);
    if (frameVisible) drawOverlay?.(ctx, w, h, w / (box?.w ?? w));
  }, [drawOverlay, sourceSize?.[0], sourceSize?.[1], box?.w, frameVisible]);

  return (
    <div className="panel" style={{ flex: 1, minWidth: 0 }}>
      <div className="panel-head" style={{ gap: 6 }}>
        <span className="dot-state" style={{ background: colour }} />
        {label}
        <span className="spacer" />
        {badges}
        {transform.scale > 1 ? (
          <button className="ghost tiny" onClick={() => onTransform({ scale: 1, x: 0, y: 0 })}>
            {transform.scale.toFixed(1)}× reset
          </button>
        ) : null}
      </div>
      <div
        ref={hostRef}
        style={{
          flex: 1, minHeight: 0, background: "#05070a", overflow: "hidden",
          display: "flex", alignItems: "center", justifyContent: "center",
          position: "relative", cursor: transform.scale > 1 ? "grab" : "default",
        }}
        onMouseDown={(e) => {
          if (transform.scale <= 1) return;
          dragRef.current = { x: e.clientX, y: e.clientY, tx: transform.x, ty: transform.y };
        }}
        onMouseMove={(e) => {
          const d = dragRef.current;
          if (d) onTransform({ ...transform, x: d.tx + (e.clientX - d.x), y: d.ty + (e.clientY - d.y) });
        }}
        onMouseUp={() => (dragRef.current = null)}
        onMouseLeave={() => (dragRef.current = null)}
      >
        {url ? (
          /* The picture box, sized by the *source* aspect ratio rather than by
             the video element's own layout. Letting the video size the wrapper
             is circular — `max-width: 100%` on a child of a content-sized flex
             box has nothing to resolve against — and it left the picture
             floating inside a wider box, which put every overlay in the wrong
             place. Giving the box the aspect ratio makes it exactly the picture,
             so `inset: 0` on the canvas and the depth layer is exact. */
          <div
            style={{
              position: "relative",
              width: box ? `${box.w}px` : "auto",
              height: box ? `${box.h}px` : "auto",
              maxWidth: "100%",
              maxHeight: "100%",
              transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
              transformOrigin: "center center",
            }}
            onClick={(e) => {
              if (!onProbe || !frameVisible) return;
              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
              onProbe((e.clientX - rect.left) / rect.width, (e.clientY - rect.top) / rect.height);
            }}
          >
            <video
              ref={attachVideo}
              src={url}
              muted={muted}
              playsInline
              preload={framePreview ? "metadata" : "auto"}
              onLoadedMetadata={onLoadedMetadata}
              onLoadedData={updateMediaReady}
              onCanPlay={updateMediaReady}
              onSeeked={updateMediaReady}
              onSeeking={() => setMediaReady(null)}
              onWaiting={() => setMediaReady(null)}
              onEmptied={() => setMediaReady(null)}
              onError={() => setMediaReady(null)}
              style={{
                width: box ? "100%" : "auto", height: box ? "100%" : "auto",
                maxWidth: "100%", maxHeight: "100%", display: "block", objectFit: "fill",
                imageRendering: transform.scale >= 3 ? "pixelated" : "auto",
              }}
            />
            {framePreview && <FramePreview {...framePreview} onReady={readyUrl => {
              setPreviewReady(readyUrl);
              framePreview.onReady(readyUrl);
            }} />}
            {/* Keep decoders mounted, but expose no analysis until this exact base frame is visible. */}
            <div data-analysis-visible={frameVisible ? "true" : "false"}
              style={{position: "absolute", inset: 0, visibility: frameVisible ? "visible" : "hidden", pointerEvents: frameVisible ? undefined : "none"}}>
              {layers}
              <canvas
                ref={canvasRef}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 1 }}
              />
            </div>
          </div>
        ) : (
          <div className="hint">No proxy for this eye yet.</div>
        )}
        {frameInfo && frameVisible ? (
          <div
            style={{
              position: "absolute", left: 6, bottom: 6, background: "#0c0e12cc",
              border: "1px solid var(--line)", borderRadius: 3, padding: "2px 6px",
              fontFamily: "var(--mono)", fontSize: 10, color: "var(--text-1)",
            }}
          >
            frame {frameInfo.index}
            {frameInfo.deltaMs != null ? (
              <span style={{ color: Math.abs(frameInfo.deltaMs) > 16.7 ? "var(--warn)" : "var(--text-2)" }}>
                {" "}· Δ {frameInfo.deltaMs >= 0 ? "+" : ""}{frameInfo.deltaMs.toFixed(1)} ms
              </span>
            ) : null}
          </div>
        ) : null}
        {footer && frameVisible ? (
          <div
            style={{
              position: "absolute", right: 6, bottom: 6, background: "#0c0e12cc",
              border: "1px solid var(--line)", borderRadius: 3, padding: "2px 6px",
              fontFamily: "var(--mono)", fontSize: 10, color: "var(--text-1)",
            }}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The overlay toggles.
 *
 * A capability the backend reports as `available` becomes a real toggle; one
 * that is `planned` stays a disabled chip; one that is `unavailable` says why
 * (a missing extra, an unfetched model file) rather than failing when pressed.
 */
export function OverlayBar({
  capabilities,
  active,
  onToggle,
  hasResult,
  settings = {},
}: {
  capabilities: { id: string; label: string; state: string; phase: number; reason?: string }[];
  active: Record<string, boolean>;
  onToggle: (id: string) => void;
  hasResult: (id: string) => boolean | null;
  settings?: Record<string, ReactNode>;
}) {
  return (
    <div className="row overlay-chips" style={{ gap: 4 }}>
      {capabilities.filter(c => c.state !== "planned" && !["analysis.pointcloud", "overlay.rectified_preview"].includes(c.id)).map((c) => {
        if (c.state === "available") {
          const ready = hasResult(c.id);
          const label = ({"analysis.hands":"Hands & arms","analysis.objects":"Objects","analysis.dense_depth":"AI depth","stream.gaze":"Gaze"} as Record<string,string>)[c.id] ?? c.label;
          const toggle = (
            <button
              key={c.id}
              className={active[c.id] ? "active" : ""}
              aria-pressed={!!active[c.id]}
              onClick={() => onToggle(c.id)}
              title={ready === false ? "Run the analysis first — the toggle opens the panel" : c.label}
            >
              {label}
              {ready === false ? <span className="badge neutral">Not run</span> : null}
            </button>
          );
          return settings[c.id] ? (
            <div key={c.id} className={`overlay-control${active[c.id] ? " active" : ""}`} role="group" aria-label={label}>
              {toggle}
              <OverlaySettings label={label}>{settings[c.id]}</OverlaySettings>
            </div>
          ) : toggle;
        }
        if (c.state === "unavailable") {
          return (
            <button key={c.id} disabled className="ghost" title={c.reason || "unavailable on this machine"}>
              {c.label}
              <Badge kind="warning">Unavailable</Badge>
            </button>
          );
        }
        return (
          <button
            key={c.id}
            disabled
            className="ghost"
            title={`Not implemented yet — planned for phase ${c.phase}. No result is shown until the analysis actually runs.`}
          >
            {c.label}
            <Badge kind="planned">Planned</Badge>
          </button>
        );
      })}
    </div>
  );
}
